import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Shield, Search, ExternalLink, AlertCircle, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE_URL = process.env.REACT_APP_BACKEND_URL;

export default function SupportAccess() {
  const [open, setOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [pin, setPin] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleValidatePin = async () => {
    if (!userEmail.trim()) {
      setError('Please enter user email');
      return;
    }
    if (!pin.trim() || pin.length !== 6) {
      setError('Please enter a valid 6-digit PIN');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${API}/support-pin/validate`, {
        user_email: userEmail,
        pin: pin,
        reason: reason || undefined
      });

      const { access_token, dashboard_path, target_user } = response.data;

      toast.success(`Access granted to ${target_user.email}'s dashboard`);

      // Open dashboard in new tab with the access token - use window.location.origin for cross-environment compatibility
      const autoLoginUrl = `${window.location.origin}/auto-login?token=${access_token}&redirect=${dashboard_path}`;
      window.open(autoLoginUrl, '_blank');

      // Reset form
      setUserEmail('');
      setPin('');
      setReason('');
      setOpen(false);

    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to validate PIN';
      setError(detail);
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  const handlePinChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setPin(value);
    setError('');
  };

  return (
    <>
      <Button
        variant="outline"
        className="gap-2 border-purple-200 text-purple-700 hover:bg-purple-600 hover:border-purple-600"
        onClick={() => setOpen(true)}
        data-testid="support-access-btn"
      >
        <Shield className="w-4 h-4" />
        Client Dashboard Access
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-purple-600" />
              Support PIN Access
            </DialogTitle>
            <DialogDescription>
              Enter the user's email and Support PIN to access their dashboard
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="user-email">User Email</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="user-email"
                  type="email"
                  placeholder="Enter user's email"
                  value={userEmail}
                  onChange={(e) => {
                    setUserEmail(e.target.value);
                    setError('');
                  }}
                  className="pl-10"
                  data-testid="support-user-email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="support-pin">6-Digit Support PIN</Label>
              <Input
                id="support-pin"
                type="text"
                inputMode="numeric"
                placeholder="000000"
                value={pin}
                onChange={handlePinChange}
                maxLength={6}
                className="text-center text-2xl tracking-[0.3em] font-mono"
                data-testid="support-pin-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Access (Optional)</Label>
              <Textarea
                id="reason"
                placeholder="e.g., Helping user with billing issue"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={2}
                data-testid="support-reason"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 rounded-lg border border-red-200 text-sm text-red-700">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleValidatePin}
              disabled={loading || !userEmail || pin.length !== 6}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="support-access-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Access Dashboard
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
